import {
  collection, query, where, getDocs, orderBy, Timestamp, onSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase';
import { fetchByTable, addDataToTable, updateDataById } from '../utils';

export const subscribeToReports = callback => {
  const q = query(collection(db, 'reports'), orderBy('createdAt', 'desc'));
  return onSnapshot(
    q,
    snap => callback(snap.docs.map(d => ({ id: d.id, ...d.data() }))),
    err => { if (err.code !== 'permission-denied') console.error('[Firestore] reports listener:', err.code); },
  );
};

export const resolveReport = async (id, resolved) =>
  updateDataById({ id, tableName: 'reports', data: { resolved } });

export const addTokens = async (id, data, setLoading) =>
  updateDataById({ id, tableName: 'users', data, setLoading });

export const fetchUsers = async setLoading =>
  fetchByTable({ tableName: 'users', orderByField: 'nickname', setLoading });

// Admin-only: change a user's role (user | manager | admin).
export const updateUserRole = async (id, role, setLoading) =>
  updateDataById({ id, tableName: 'users', data: { role }, setLoading });

// Admin-only: block/unblock a user. When blocked, the app renders a lock
// screen instead of the normal UI (see App.jsx) and Firestore rules reject
// writes from that account.
export const updateUserBlocked = async (id, blocked, setLoading) =>
  updateDataById({ id, tableName: 'users', data: { blocked }, setLoading });

export const addPlace = async (data, setLoading) =>
  addDataToTable({ tableName: 'fields', data, setLoading });

// Finished shifts within [start,end] that have at least one post-match
// survey response. Used by SurveysTab. Sorted newest first client-side.
export const fetchFinishedShiftsInRange = async (startDate, endDate, setLoading) => {
  setLoading?.(true);
  try {
    const q = query(
      collection(db, 'shifts'),
      where('isFinished', '==', true),
      where('startDate', '>=', Timestamp.fromDate(startDate)),
      where('startDate', '<=', Timestamp.fromDate(endDate)),
    );
    const snap = await getDocs(q);
    const rows = snap.docs.map(d => ({ ...d.data(), id: d.id }));
    rows.sort((a, b) => {
      const at = a.startDate?.toMillis?.() ?? 0;
      const bt = b.startDate?.toMillis?.() ?? 0;
      return bt - at;
    });
    return rows.filter(s => s.postMatch?.responses && Object.keys(s.postMatch.responses).length > 0);
  } finally {
    setLoading?.(false);
  }
};

// Toggles the admin-verified flag on a shift's postMatch bundle so we
// know a human reviewed the responses (and either fixed stats or
// confirmed everything was correct).
export const setPostMatchVerified = async (shiftId, verified, adminUid) =>
  updateDataById({
    id: shiftId,
    tableName: 'shifts',
    data: {
      'postMatch.verified': verified,
      'postMatch.verifiedBy': verified ? adminUid : null,
      'postMatch.verifiedAt': verified ? Timestamp.now() : null,
    },
  });

export const fetchShiftsByDateRange = async (startDate, endDate, userId, setLoading) => {
  setLoading(true);
  try {
    // No orderBy on the server: it would need a (userId ASC, startDate DESC)
    // composite index, and when that index isn't provisioned Firestore returns
    // FAILED_PRECONDITION which we caught silently — the finance tab looked
    // empty even when the user had matches this month. Sort client-side.
    const q = query(
      collection(db, 'shifts'),
      where('userId', '==', userId),
      where('startDate', '>=', Timestamp.fromDate(startDate)),
      where('startDate', '<=', Timestamp.fromDate(endDate)),
    );
    const snap = await getDocs(q);
    const rows = snap.docs.map(d => ({ ...d.data(), id: d.id }));
    rows.sort((a, b) => {
      const at = a.startDate?.toMillis?.() ?? 0;
      const bt = b.startDate?.toMillis?.() ?? 0;
      return bt - at;
    });
    return rows;
  } finally {
    setLoading(false);
  }
};
